import { z } from 'zod';
import { PAGINATION_SORT_DIRECTION } from '~/constants';

import {
  ORDER_BY_STOPPER_RULE,
  REPROCESSING_INVOICE_CHANNEL,
  REPROCESSING_INVOICE_ORDER_BY,
  REPROCESSING_INVOICE_STATUS,
} from '~/data/invoice-reprocessing';

import { PREDEFINED_VIEWS } from '~/modules/invoice-reprocessing/list/constants';

import { ValidUuidOrNotSetSchema } from '@vestigas/utils';
import { DATE_RANGE_LABELS, FILTER_DATE_RANGES } from '~/utils';

export const reprocessingListDefaultValues = {
  dateRangeLabel: 'last30Days',
  page: 0,
  sortModel: {
    orderBy: 'created_on',
    sortDirection: 'DESC',
  },
};
const defaultDateRange =
  FILTER_DATE_RANGES[reprocessingListDefaultValues.dateRangeLabel];

/*
 * Validates the search parameters for the invoice reprocessing list page
 * both when updating and when reading from the URL.
 */
export const zodInvoiceReprocessingListSearchParamsSchema = z
  .object({
    channel: z
      .array(z.enum(REPROCESSING_INVOICE_CHANNEL))
      .optional()
      .catch(undefined),
    createdAfter: z.iso
      .datetime()
      .optional()
      .prefault(defaultDateRange.start)
      .catch(defaultDateRange.start),
    createdBefore: z.iso
      .datetime()
      .optional()
      .prefault(defaultDateRange.end)
      .catch(defaultDateRange.end),
    dateRangeLabel: z
      .enum(DATE_RANGE_LABELS)
      .optional()
      .catch(reprocessingListDefaultValues.dateRangeLabel),
    dryRun: z.boolean().optional().catch(undefined),
    needsAiParsing: z.boolean().optional().catch(undefined),
    orderBy: z
      .enum(REPROCESSING_INVOICE_ORDER_BY)
      .optional()
      .prefault(reprocessingListDefaultValues.sortModel.orderBy)
      .catch(reprocessingListDefaultValues.sortModel.orderBy),
    page: z
      .number()
      .nonnegative()
      .optional()
      .prefault(reprocessingListDefaultValues.page)
      .catch(reprocessingListDefaultValues.page),
    searchString: z.string().trim().optional().catch(undefined),
    sortOrder: z
      .enum(PAGINATION_SORT_DIRECTION)
      .optional()
      .prefault(reprocessingListDefaultValues.sortModel.sortDirection)
      .catch(reprocessingListDefaultValues.sortModel.sortDirection),
    status: z
      .array(z.enum(REPROCESSING_INVOICE_STATUS))
      .optional()
      .catch(undefined),
    viewId: z
      .enum(PREDEFINED_VIEWS.map(({ id }) => id))
      .optional()
      .catch(undefined), // TODO: Add enum?
  })
  .refine((data) => {
    if (data.dateRangeLabel) {
      const dateRange = FILTER_DATE_RANGES[data.dateRangeLabel];
      if (dateRange) {
        // Ensure the corresponding dates are set, if a dateRangeLabel is set
        data.createdAfter = dateRange.start;
        data.createdBefore = dateRange.end;
      }
    }

    return data;
  });

/*
 * Validates the search parameters for the invoice reprocessing edit page
 * both when updating and when reading from the URL.
 */
export const zodInvoiceReprocessingEditSearchParamsSchema = z.object({
  contentLeft: z.string().optional().catch(undefined),
  contentRight: z.string().optional().catch(undefined),
});

export const zodInvoiceStoppersListSearchParamsSchema = z.object({
  buyerCompanyAccountId: ValidUuidOrNotSetSchema.catch(undefined),
  buyerCompanyId: ValidUuidOrNotSetSchema.catch(undefined),
  isActive: z.boolean().nullish().prefault(true).catch(true),
  orderBy: z
    .enum(ORDER_BY_STOPPER_RULE)
    .optional()
    .prefault('created_on')
    .catch('created_on'),
  page: z.number().nonnegative().optional().prefault(0).catch(0),
  searchString: z.string().trim().optional().catch(undefined),
  sellerCompanyAccountId: ValidUuidOrNotSetSchema.catch(undefined),
  sellerCompanyId: ValidUuidOrNotSetSchema.catch(undefined),
  sortOrder: z
    .enum(PAGINATION_SORT_DIRECTION)
    .optional()
    .prefault('DESC')
    .catch('DESC'),
});
